import { useMemo } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * A custom report's result: `{columns: [{key, label, type}], rows, totals,
 * truncated, row_limit, provenance}`. The server names every column's type, so a cell is
 * formatted by what it is, not guessed from its name.
 */
const NUMERIC = new Set(["money", "number"]);

const dateText = value =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString("en-GB", { timeZone: "UTC", day: "2-digit", month: "short", year: "numeric" });

// Datetimes arrive as the restaurant's wall clock with no offset: shown as sent.
const datetimeText = value =>
  new Date(`${value}Z`).toLocaleString("en-GB", {
    timeZone: "UTC", day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });

export const cellText = (type, value) => {
  if (value === null || value === undefined || value === "") return "—";
  switch (type) {
    case "money":
      return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case "number":
      return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    case "date":
      return dateText(value);
    case "datetime":
      return datetimeText(value);
    case "bool":
      return value ? "Yes" : "No";
    default:
      return String(value);
  }
};

/** Excel and LibreOffice execute a cell that opens with one of these. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;
const csvCell = value => {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (typeof value === "string" && FORMULA_LEAD.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

export const toCsv = result => {
  const { columns, rows, totals } = result;
  const lines = [
    columns.map(c => csvCell(c.label)).join(","),
    ...rows.map(row => columns.map(c => csvCell(row[c.key])).join(",")),
  ];
  if (totals) lines.push(columns.map((c, i) => (i === 0 && !(c.key in totals) ? "Total" : csvCell(totals[c.key]))).join(","));
  return lines.join("\r\n");
};

export default function CustomReportTable({
  result,
  canExport = true,
  filename = "custom-report",
  testId = "custom-report-table",
  onCellClick = null,
  onExportXlsx = null,
}) {
  const gridStyle = useMemo(() => {
    const count = result?.columns?.length || 1;
    return {
      gridTemplateColumns: Array.from({ length: count }, (_, i) => (i ? "minmax(96px,1fr)" : "var(--report-first-col, minmax(150px,1.6fr))")).join(" "),
      minWidth: `${Math.max(480, count * 132)}px`,
    };
  }, [result]);

  if (!result) return null;
  const { columns, rows, totals, truncated, row_limit: rowLimit, provenance } = result;

  const downloadCsv = () => {
    const blob = new Blob([toCsv(result)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename.replace(/[^\w-]+/g, "-").toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const align = c => (NUMERIC.has(c.type) ? { textAlign: "right", fontVariantNumeric: "tabular-nums" } : undefined);

  return (
    <div className="space-y-snug" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-snug">
        <div className="flex items-center gap-2">
          <p className="text-meta text-muted-foreground" data-testid={`${testId}-count`}>
            {rows.length.toLocaleString("en-IN")} {rows.length === 1 ? "row" : "rows"}
          </p>
          {provenance && (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-micro font-medium border ${
                provenance === "live"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300"
                  : provenance === "mixed"
                  ? "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300"
                  : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300"
              }`}
              title={`Data provenance: ${provenance}`}
            >
              {provenance === "live" ? "Live Data" : provenance === "mixed" ? "Mixed (Live + Historical)" : "Imported Data"}
            </span>
          )}
        </div>
        {canExport && (
          <div className="flex items-center gap-2">
            {onExportXlsx && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExportXlsx}
                disabled={!rows.length}
                data-testid={`${testId}-download-xlsx`}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-tight" /> Excel (.xlsx)
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCsv}
              disabled={!rows.length}
              data-testid={`${testId}-download`}
            >
              <Download className="w-3.5 h-3.5 mr-tight" /> Download CSV
            </Button>
          </div>
        )}
      </div>

      {truncated && (
        <p className="text-meta text-warning" role="status" data-testid={`${testId}-truncated`}>
          Showing the first {Number(rowLimit).toLocaleString("en-IN")} rows only. Narrow the dates or add a filter to see the rest.
        </p>
      )}

      {rows.length === 0 ? (
        <p className="text-body text-muted-foreground" data-testid={`${testId}-empty`}>
          No rows for these dates and filters.
        </p>
      ) : (
        <div className="report-detail-table scroll-contain-x">
          <header style={gridStyle}>
            {columns.map((c, i) => (
              <span key={c.key} className={i === 0 ? "report-cell-sticky" : undefined} style={align(c)}>
                {c.label}
              </span>
            ))}
          </header>
          {rows.map((row, index) => (
            <div key={index} style={gridStyle} data-testid={`${testId}-row-${index}`}>
              {columns.map((c, i) => {
                const text = cellText(c.type, row[c.key]);
                const long = text.length > 60;
                const isDrillable = onCellClick && (c.key === "classification" || (i === 0 && text !== "—"));

                return (
                  <span
                    key={c.key}
                    title={long ? text : isDrillable ? "Click to view underlying transactions" : undefined}
                    className={[
                      i === 0 && "report-cell-sticky",
                      long && "report-cell-clamp",
                      isDrillable && "cursor-pointer text-primary hover:underline font-medium",
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined}
                    style={align(c)}
                    onClick={() => isDrillable && onCellClick(row[c.key], c, row)}
                  >
                    {text}
                  </span>
                );
              })}
            </div>
          ))}
          {totals && (
            <div className="report-pack-totals-row" style={gridStyle} data-testid={`${testId}-totals`}>
              {columns.map((c, i) => (
                <span key={c.key} style={align(c)}>
                  {c.key in totals ? cellText(c.type, totals[c.key]) : i === 0 ? (truncated ? "Total of rows shown" : "Total") : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
